import { createContext, useContext, type ReactNode } from 'react'
import type { IDeploymentEngine } from './IDeploymentEngine.interface'
import { MockDeploymentEngine } from './MockDeploymentEngine'
import { strideDeploymentEngine } from './StrideDeploymentEngine'

// ── Engine resolution ─────────────────────────────────────────────────────────
// VITE_DEPLOYMENT_ENGINE controls which engine is used at runtime.
// Default: 'mock' for local development.
// Set to 'stride' in the Azure DevOps pipeline variable group.

function resolveEngine(): IDeploymentEngine {
  const key = import.meta.env.VITE_DEPLOYMENT_ENGINE ?? 'mock'
  switch (key) {
    case 'stride': return strideDeploymentEngine
    case 'mock':
    default:          return new MockDeploymentEngine()
  }
}

const engine = resolveEngine()

// ── Context ───────────────────────────────────────────────────────────────────

const DeploymentEngineContext = createContext<IDeploymentEngine>(engine)

interface DeploymentEngineProviderProps {
  children: ReactNode
  /** Override the engine instance — used in tests. */
  engine?: IDeploymentEngine
}

export function DeploymentEngineProvider({ children, engine: override }: DeploymentEngineProviderProps) {
  return (
    <DeploymentEngineContext.Provider value={override ?? engine}>
      {children}
    </DeploymentEngineContext.Provider>
  )
}

export function useDeploymentEngine(): IDeploymentEngine {
  return useContext(DeploymentEngineContext)
}
